import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUp, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import youtubeImg from '@/assets/img/youtube.png';
import LanguageSwitcher from '@/components/shared/LanguageSwitcher';
import { ROUTES } from '@/lib/constants/routes';

export type { SupportedLocale } from '@/lib/i18nLocale';

const serviceLinks = [
  { labelKey: 'footer:openClasses', to: ROUTES.COMMON.CLASS_LIST },
  { labelKey: 'footer:resources', to: ROUTES.COMMON.RESOURCES },
  { labelKey: 'footer:blog', to: ROUTES.COMMON.BLOG },
] as const;

const BUSINESS_INFO_URL = 'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=1208808376';
const WHATSAPP_URL = 'https://wa.me/qr/MW7PEFBMFHTTB1';

export default function Footer(): React.ReactElement {
  const { t, i18n } = useTranslation(['footer']);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isKorean = i18n.language.toLowerCase().startsWith('ko');

  useEffect(() => {
    const scrollContainer = document.querySelector<HTMLElement>('[data-page-scroll-container]');
    const scrollTarget: HTMLElement | Window = scrollContainer ?? window;
    const handleScroll = () => {
      setShowScrollTop((scrollContainer?.scrollTop ?? window.scrollY) > 320);
    };

    handleScroll();
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    const scrollContainer = document.querySelector<HTMLElement>('[data-page-scroll-container]');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-white border-t border-gray-200 py-12 px-4 text-sm text-gray-700">
      <div className="max-w-[1460px] mx-auto">
        <div className="grid grid-cols-1 gap-8 mb-12 md:grid-cols-[180px_140px_minmax(280px,1fr)_auto] lg:gap-10">
          <span className="text-xl font-bold text-gray-900">© NEWBASE Inc.</span>
          <div>
            <h4 className="font-bold text-gray-900 mb-4 text-base">{t('footer:serviceTitle')}</h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-900 hover:text-brand-600 hover:underline">
                    {t(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-base font-bold">
              <Link to={ROUTES.COMMON.CONTACT} className="inline-flex items-center gap-1 text-gray-900 hover:underline">
                <span>{t('footer:contactTitle')}</span>
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </h4>
            <ul className="space-y-2">
              {isKorean ? (
                <li className="flex items-center gap-2">
                  <Phone size={15} className="shrink-0 text-gray-900" aria-hidden="true" />
                  <span>{t('footer:tel')}</span>
                </li>
              ) : (
                <li className="flex items-center gap-2">
                  <MessageCircle size={15} className="shrink-0 text-gray-900" aria-hidden="true" />
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-900 hover:text-brand-600 hover:underline"
                  >
                    {t('footer:whatsapp')}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-2">
                <Mail size={15} className="shrink-0 text-gray-900" aria-hidden="true" />
                <span>{t('footer:email')}</span>
              </li>
              <li className="flex items-start gap-2 leading-relaxed">
                <MapPin size={15} className="mt-0.5 shrink-0 text-gray-900" aria-hidden="true" />
                <span>{t('footer:address')}</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-row items-center gap-2 md:justify-end">
            <a href="https://youtube.com/@newbase9254?si=KD7rk0OFcN_eW36k" target="_blank" rel="noopener noreferrer" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm transition-colors hover:bg-gray-50" aria-label="YouTube">
              <img src={youtubeImg} alt="YouTube" className="h-6 w-6 object-contain" />
            </a>
            <LanguageSwitcher variant="labeled" labelType="current" tone="neutral" align="end" />
          </div>
        </div>

        <div className="flex flex-col gap-6 border-t border-gray-200 pt-6 text-sm leading-6 text-gray-500 md:flex-row md:items-start md:justify-between">
          <div>
            <p>
              <span className="font-semibold text-gray-600 w-28 inline-block">{t('footer:businessName')}</span>
              {t('footer:businessDetails')}
              {isKorean && (
                <>
                  {' '}
                  <a
                    href={BUSINESS_INFO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-gray-600 hover:text-gray-900 hover:underline"
                  >
                    {t('footer:businessInfoCheck')}
                  </a>
                </>
              )}
            </p>
          </div>

          <nav className="shrink-0 md:text-right" aria-label={t('footer:legalTitle')}>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 md:justify-end">
              <li><Link to={ROUTES.COMMON.TERMS} className="text-gray-500 hover:text-gray-900 hover:underline">{t('footer:terms')}</Link></li>
              <li><Link to={ROUTES.COMMON.PRIVACY} className="text-gray-500 hover:text-gray-900 hover:underline">{t('footer:privacy')}</Link></li>
            </ul>
          </nav>
        </div>
      </div>

      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-lg transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          aria-label={t('footer:scrollTop')}
          title={t('footer:scrollTop')}
        >
          <ArrowUp size={20} aria-hidden="true" />
        </button>
      )}
    </footer>
  );
}
